import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose,
} from "@/components/ui/dialog"
import { Button } from "@/components/button"
import { toast } from "sonner"
import { useEffect, useState } from "react"
import { useUpdateFlashcardModal } from "@/modules/flashcards/stores/use-update-flashcard-modal-store"
import { Textarea } from "@/components/ui/textarea"
import useGetFlashcard from "../../queries/use-get-flashcard"
import useUpdateDeck from "../../queries/use-update-deck"

type Option = {
  answer: string
  question: string
  error: string
  id: string
}

const UpdateFlashcardModal = () => {
  const { isOpen, flashcardId, closeModal } = useUpdateFlashcardModal()
  const { isPending, mutateAsync } = useUpdateDeck(flashcardId)
  const [options, setOptions] = useState<Option[]>([])
  const { data, isSuccess, isFetching } = useGetFlashcard(flashcardId)
  const areLoading = isPending || isFetching

  useEffect(() => {
    if (isSuccess && data.flashcards.length > 0) {
      const options = data.flashcards.map((opt) => ({
        ...opt,
        error: "",
      }))
      setOptions(options)
    }
  }, [isSuccess, data])

  const handleOnClose = () => {
    if (areLoading) return
    closeModal()
    setOptions([])
  }

  const handleOnSave = async () => {
    try {
      const body = {
        flashcards: options.map((opt) => ({
          question: opt.question,
          answer: opt.answer,
          id: opt.id,
        })),
      }
      await mutateAsync({ deckId: flashcardId, body })
      toast.success("La flashcard ha sido editada correctamente.")
    } catch {
      toast.error("Ha ocurrido un error al editar la flashcard.")
    } finally {
      closeModal()
    }
  }

  const handleOnChangeInput = (
    optionTouched: Option,
    e: React.ChangeEvent<HTMLTextAreaElement>,
  ) => {
    const newValue = e.target.value
    const name = e.target.name
    const newOptions = options.map((option) => {
      if (option.id !== optionTouched.id) {
        return option
      } else {
        return {
          ...option,
          [name]: newValue,
          error: newValue.trim() === "" ? "No pueden haber campos vacíos." : "",
        }
      }
    })

    setOptions(newOptions)
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleOnClose}>
      <DialogContent className="flex max-h-[90vh] w-[95%] max-w-[700px] flex-col gap-3 overflow-hidden">
        <DialogHeader className="flex shrink-0 items-start">
          <DialogTitle>Editar flashcard</DialogTitle>
        </DialogHeader>
        <DialogDescription className="flex size-full flex-col gap-5 overflow-y-auto overflow-x-hidden">
          {isFetching ? (
            <div className="flex h-[400px] w-full items-center justify-center">
              <p>Cargando...</p>
            </div>
          ) : (
            options.map((option, i) => {
              return (
                <div className="rounded-[8px] border border-neutral-300 p-4" key={option.id}>
                  <p className="mb-2 text-base font-bold text-black">Carta {i + 1}</p>
                  <div className="flex flex-col gap-3">
                    <div>
                      <span className="mb-1 block text-xs">Pregunta</span>
                      <Textarea
                        value={option.question}
                        name="question"
                        onChange={(e) => handleOnChangeInput(option, e)}
                        className="h-fit max-h-[300px] !min-h-[80px] w-full resize-none overflow-y-auto overflow-x-hidden"
                        disabled={areLoading}
                      />
                    </div>
                    <div>
                      <span className="mb-1 block text-xs">Respuesta</span>
                      <Textarea
                        value={option.answer}
                        name="answer"
                        onChange={(e) => handleOnChangeInput(option, e)}
                        className="h-fit max-h-[300px] !min-h-[150px] w-full resize-none overflow-y-auto overflow-x-hidden"
                        disabled={areLoading}
                      />
                    </div>
                  </div>
                  {option.error && (
                    <span className="mt-2 block text-xs text-red-500">{option.error}</span>
                  )}
                </div>
              )
            })
          )}
        </DialogDescription>
        <DialogFooter className="flex h-full shrink-0 flex-row items-center justify-end gap-1 overflow-hidden">
          <DialogClose asChild>
            <Button type="button" className="bg-gray-300 text-black" disabled={areLoading}>
              Cancelar
            </Button>
          </DialogClose>

          <Button
            type="submit"
            onClick={handleOnSave}
            loading={isPending}
            disabled={areLoading || options.some((opt) => opt.error)}
          >
            Guardar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default UpdateFlashcardModal
